<template>
  <div class="paywrap">
    <div class="card">
      <div class="brand">CHDS</div>

      <div v-if="loading" class="state">Loading…</div>

      <div v-else-if="notfound" class="state err">
        <i class="fas fa-triangle-exclamation"></i>
        This payment link isn’t valid. Please check with our team.
      </div>

      <div v-else-if="paid" class="state done">
        <i class="fas fa-circle-check"></i>
        <h1>Payment received</h1>
        <p>Thank you — your payment for order <b>{{ info.order_id }}</b> is complete.</p>
      </div>

      <template v-else>
        <h1>Pay your order balance</h1>
        <p class="sub">Order <b>{{ info.order_id }}</b></p>
        <div class="amount">{{ money(info.amount) }} <span class="cur">{{ info.currency }}</span></div>
        <p v-if="info.note" class="note">{{ info.note }}</p>

        <label class="clabel">Card details</label>
        <div id="card-el" class="cardbox"></div>
        <div v-if="payError" class="perr">{{ payError }}</div>

        <button class="pay" @click="pay" :disabled="paying || !cardReady">
          <i class="fas fa-lock"></i> {{ paying ? 'Processing…' : 'Pay ' + money(info.amount) }}
        </button>
        <p class="secure"><i class="fas fa-shield-halved"></i> Secure payment by Stripe. Your card is never stored by CHDS.</p>
      </template>
    </div>
  </div>
</template>

<script>
import caxios from '../../axios';
import { stripeFor } from '@/stripe.js';
import { useSEO } from '@/composables/useSEO';

export default {
  name: 'PayBalance',
  setup() {
    useSEO({ title: 'Pay your CHDS balance', description: 'Pay your CHDS balance', noindex: true, path: '/pay' });
  },
  data() {
    return {
      token: this.$route.params.token,
      info: null, loading: true, notfound: false, paid: false,
      stripe: null, card: null, cardReady: false,
      paying: false, payError: '', clientSecret: '',
    };
  },
  async created() { await this.loadInfo(); },
  methods: {
    money(v) { return '$' + (Math.round((Number(v) || 0) * 100) / 100).toFixed(2); },
    async loadInfo() {
      this.loading = true; this.notfound = false;
      try {
        const { data } = await caxios.get(`/pay/${this.token}/`);
        this.info = data;
        if (data.status === 'paid') { this.paid = true; }
        else { this.$nextTick(() => this.mountCard()); }
      } catch (e) {
        this.notfound = true;
      } finally { this.loading = false; }
    },
    async mountCard() {
      try {
        this.stripe = await stripeFor();
        const elements = this.stripe.elements();
        this.card = elements.create('card', { style: { base: { fontSize: '16px', color: '#1f2421' } } });
        this.card.mount('#card-el');
        this.card.on('ready', () => { this.cardReady = true; });
        this.card.on('change', (e) => { this.payError = e.error ? e.error.message : ''; });
      } catch (e) { this.payError = 'Could not load the secure card form. Please refresh.'; }
    },
    async pay() {
      if (this.paying) return;
      this.paying = true; this.payError = '';
      try {
        if (!this.clientSecret) {
          const { data } = await caxios.post(`/pay/${this.token}/intent/`, {});
          this.clientSecret = data.client_secret;
        }
        const result = await this.stripe.confirmCardPayment(this.clientSecret, { payment_method: { card: this.card } });
        if (result.error) {
          this.payError = result.error.message || 'Your card could not be charged. Please try again.';
        } else if (result.paymentIntent && result.paymentIntent.status === 'succeeded') {
          this.paid = true;
        } else {
          this.payError = 'Payment did not complete. Please try again.';
        }
      } catch (e) {
        const s = e.response && e.response.status;
        if (s === 409) { this.payError = 'This balance has already been paid.'; this.paid = true; }
        else { this.payError = e.response?.data?.detail || 'Something went wrong. Please try again.'; }
      } finally { this.paying = false; }
    },
  },
};
</script>

<style scoped>
.paywrap { min-height: 100vh; display: flex; align-items: center; justify-content: center; background: #f4f6f0; padding: 20px; }
.card { width: 100%; max-width: 420px; background: #fff; border: 1px solid #e7e8e3; border-radius: 18px; padding: 26px 24px; box-shadow: 0 6px 26px rgba(31,36,33,.08); }
.brand { font-weight: 800; letter-spacing: 1px; color: #446936; font-size: 20px; margin-bottom: 18px; }
h1 { font-size: 20px; margin: 0 0 4px; color: #1f2421; }
.sub { font-size: 13px; color: #8a908a; margin: 0 0 14px; }
.amount { font-size: 34px; font-weight: 800; color: #1f2421; }
.amount .cur { font-size: 14px; font-weight: 600; color: #8a908a; }
.note { font-size: 13px; color: #5a615b; margin: 8px 0 0; }
.clabel { display: block; font-size: 12px; color: #8a908a; margin: 20px 0 6px; }
.cardbox { border: 1px solid #dfe1da; border-radius: 10px; padding: 14px 12px; }
.pay { width: 100%; min-height: 52px; margin-top: 16px; border: none; border-radius: 12px; background: #446936; color: #fff; font-size: 16px; font-weight: 700; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px; }
.pay:disabled { opacity: .55; cursor: default; }
.secure { font-size: 11.5px; color: #a3a8a1; text-align: center; margin: 12px 0 0; }
.perr { color: #a32d2d; font-size: 12.5px; margin-top: 8px; }
.state { text-align: center; padding: 20px 0; color: #5a615b; font-size: 14px; }
.state.err { color: #a32d2d; }
.state.done i { font-size: 44px; color: #446936; }
.state.done h1 { margin: 12px 0 6px; }
.state i { font-size: 20px; }
</style>
